import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import 'react-image-crop/dist/ReactCrop.css';
import PropTypes from 'prop-types';
import Button from '../button/button';
import style from './cropImage.module.scss';

const TO_RADIANS = Math.PI / 180;

// Generic function to apply crop, scale, and rotation transformations
export async function cropImagePreview(image, canvas, crop, scale = 1, rotate = 0) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  const newCanvas = canvas; // shallow clone the canvas object
  newCanvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  newCanvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  ctx.translate(-cropX, -cropY); // Move crop area to origin
  ctx.translate(centerX, centerY); // Move origin to image center
  ctx.rotate(rotateRads); // Rotate around the origin
  ctx.scale(scale, scale); // Apply scaling
  ctx.translate(-centerX, -centerY); // Move back the center
  ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

  ctx.restore();
}

const CropImage = ({ image, handleSaveCropImage, selectedFile, onClose, isRotate, isLoading }) => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [lock, setLock] = useState(false);
  const [aspect, setAspect] = useState(16 / 9);
  const [rotate, setRotate] = useState(0);
  const scale = 1;

  const centerAspectCrop = (mediaWidth, mediaHeight, aspectRatio) => centerCrop(
      makeAspectCrop(
        { unit: '%', width: 90 },
        aspectRatio,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    if (aspect) {
      setCrop(centerAspectCrop(width, height, aspect));
    }
  };

  useEffect(() => {
    if (completedCrop && imgRef.current && previewCanvasRef.current) {
      cropImagePreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
        rotate
      );
    }
  }, [completedCrop, scale, rotate]);

  const saveCroppedImage = async () => {
    try {
      if (!completedCrop || !imgRef.current) return;

      const canvas = document.createElement('canvas');
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      const pixelRatio = window.devicePixelRatio;

      canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
      canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context is not available');
      }

      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingQuality = 'high';

      const cropX = completedCrop.x * scaleX;
      const cropY = completedCrop.y * scaleY;

      const rotateRads = rotate * TO_RADIANS;
      const centerX = imgRef.current.naturalWidth / 2;
      const centerY = imgRef.current.naturalHeight / 2;

      ctx.save();

      ctx.translate(-cropX, -cropY); // Move crop to origin
      ctx.translate(centerX, centerY); // Move origin to image center
      ctx.rotate(rotateRads); // Rotate
      ctx.scale(scale, scale); // Scale
      ctx.translate(-centerX, -centerY); // Reset origin
      ctx.drawImage(
        imgRef.current,
        0,
        0,
        imgRef.current.naturalWidth,
        imgRef.current.naturalHeight
      );

      ctx.restore();

      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          const croppedFile = new File([blob], `${selectedFile.name}`, {
            type: selectedFile.type,
            lastModified: Date.now()
          });
          croppedFile.preview = URL.createObjectURL(blob);
          handleSaveCropImage(croppedFile);
        },
        selectedFile.type,
        1
      );
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAspectChange = (newAspect) => {
    setAspect(newAspect);
    if (imgRef.current && newAspect) {
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, newAspect));
    } else {
      setAspect(undefined);
    }
  };

  return (
    <>
      <div className={style.cropContainer}>
        <div className={style.imageContainer}>
          <div>
            <h4>
              <FormattedMessage id="crop.original" defaultMessage="Original" />
            </h4>
            {image && (
              <div className={style.cropper}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={c => setCompletedCrop(c)}
                  aspect={aspect}
                  minWidth={50}
                  minHeight={50}
                  locked={lock}
                >
                  <img
                    ref={imgRef}
                    src={image}
                    alt="Crop"
                    onLoad={onImageLoad}
                    style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                  />
                </ReactCrop>
              </div>
            )}
          </div>
          <div>
            <h4>
              <FormattedMessage id="crop.preview" defaultMessage="Preview" />
            </h4>
            <canvas
              ref={previewCanvasRef}
              className={style.preview}
              style={{
                width: completedCrop?.width,
                height: completedCrop?.height
              }}
            />
          </div>
        </div>
        <div className={style.controls}>
          <div className={style.aspectRatioButtons}>
            <button onClick={() => handleAspectChange(16 / 9)} className={aspect === 16 / 9 ? style.highlighted : ''}>16:9</button>
            <button onClick={() => handleAspectChange(4 / 3)} className={aspect === 4 / 3 ? style.highlighted : ''}>4:3</button>
            <button onClick={() => handleAspectChange(1)} className={aspect === 1 ? style.highlighted : ''}>1:1</button>
            <button onClick={() => handleAspectChange(undefined)} className={aspect === undefined ? style.highlighted : ''}>
              <FormattedMessage id="crop.custom" defaultMessage="Custom" />
            </button>
            <button onClick={() => setLock(!lock)} className={lock ? style.highlighted : ''}>
              <FormattedMessage id="crop.lock" defaultMessage="Lock" />
            </button>
          </div>
          <div className={style.sliders}>
            {isRotate && (
              <div>
                <label htmlFor="rotate">
                  <FormattedMessage id="crop.rotate" defaultMessage="Rotate" />
                </label>
                <input
                  id="rotate"
                  type="range"
                  min={0}
                  max={360}
                  value={rotate}
                  onChange={e => setRotate(Number(e.target.value))}
                />
                {rotate}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={style.btnContainer}>
        <Button
          action={'tertiary'}
          onClick={onClose}
          translationID="inspections.new.cancelBtn"
          defaultText="Cancel"
        />
        <Button
          action={'secondary'}
          onClick={saveCroppedImage}
          translationID={'shifts.timepicker.save'}
          defaultText={'Save'}
          disabled={!completedCrop || !imgRef.current || isLoading}
        />
      </div>
    </>
  );
};

CropImage.propTypes = {
  image: PropTypes.string.isRequired,
  handleSaveCropImage: PropTypes.func.isRequired,
  selectedFile: PropTypes.instanceOf(File).isRequired
};

export default CropImage;
